'use client'

import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react'
import { getAccessToken, useUser } from '@auth0/nextjs-auth0'
import { User } from '@auth0/nextjs-auth0/types'

import { createApi, type ApiClient } from '@/api/v1'

interface ApiContextType {
  api: ApiClient
  userInitialized?: User | null
  isReady: boolean
}

const ApiContext = createContext<ApiContextType>({
  api: createApi(),
  userInitialized: null,
  isReady: false
})

interface ApiProviderProps {
  children: ReactNode
}

export const ApiProvider = ({ children }: ApiProviderProps) => {
  const { user, isLoading } = useUser()
  const [api, setApi] = useState<ApiClient>(() => createApi()) // Initialize with anonymous API
  const [isReady, setIsReady] = useState<boolean>(false)

  useEffect(() => {
    const initializeApi = async () => {
      // Don't initialize API while Auth0 is still loading
      if (isLoading) {
        setIsReady(false)
        return
      }

      try {
        if (user) {
          const accessToken = await getAccessToken()
          const apiInstance = createApi(accessToken)
          setApi(apiInstance)
        } else {
          const apiInstance = createApi(undefined)
          setApi(apiInstance)
        }
        setIsReady(true)
      } catch (error) {
        console.error('Failed to initialize API:', error)
        setIsReady(false)
      }
    }

    initializeApi()
  }, [user, isLoading])

  const contextValue: ApiContextType = useMemo(
    () => ({
      api,
      userInitialized: user && isReady ? user : null,
      isReady
    }),
    [api, user, isReady]
  )

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>
}

// Hook to use API
export const useClientApi = () => {
  const context = useContext(ApiContext)

  if (!context) {
    throw new Error('useClientApi must be used within an ApiProvider')
  }

  return {
    api: context.api, // Always available, never null
    userInitialized: context.userInitialized
  }
}
